import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { Button } from "@/aible";
import Select from "react-select";
import { LineInput } from "@/components/Inputs/LineInput";
import styles from "@/components/LanguageModelArtifact/style.module.css";
import { useDispatch, useSelector } from "@/types/store";
import * as aiServiceReducer from "@/containers/AiService/reducer";
import { keysOf } from "@/utils/collect";
import { customMapper, isOptionType, selectOnChangeWrapperIfPresent } from "@/utils/select";
import * as foldersReducer from "@/containers/Folders/reducer";
import * as contentGenerationReducer from "@/containers/ContentGeneration/reducer";
import * as fineTuningReducer from "@/containers/FineTuning/reducer";
import { FineTuningExecutionType, MODEL_EVALUATION_TASK_LABEL_MAP } from "@/types/content_generation";
const EVALUATION_TASK_MAPPER = customMapper((t) => MODEL_EVALUATION_TASK_LABEL_MAP[t]);
const ModelEvaluationModal = ({ open, onOpenChange, defaultModelId, defaultDatasetId }) => {
    const dispatch = useDispatch();
    const aiServiceModels = useSelector((state) => aiServiceReducer.getModels(state));
    const modelOptions = keysOf(aiServiceModels)
        ?.filter((aiServiceId) => aiServiceModels[aiServiceId]?.type === "CONTENT_GENERATION" &&
        (aiServiceModels[aiServiceId]?.status === "ENABLED" || aiServiceModels[aiServiceId]?.status === "DISABLED"))
        ?.map((id) => ({
        label: aiServiceModels[id]?.name,
        value: aiServiceModels[id]?.version_id
    }));
    const selectedFolderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const exampleSetSelector = useSelector((state) => contentGenerationReducer.getExampleSets(state));
    const exampleSets = contentGenerationReducer.filterExampleSets(exampleSetSelector, selectedFolderId);
    const exampleSetsOptions = exampleSets.map((exampleSet) => ({
        label: exampleSet?.name,
        value: exampleSet?.id
    }));
    const [modelId, setModelId] = useState(defaultModelId);
    const [datasetId, setDatasetId] = useState(defaultDatasetId);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isLoading, updateIsLoading] = useState(false);
    const [evaluationTask, updateEvaluationTask] = useState("custom");
    useEffect(() => {
        if (modelId && datasetId) {
            const datasetName = exampleSets.find((exampleSet) => exampleSet.id === datasetId)?.name;
            const modelName = Object.values(aiServiceModels)?.find((aiServiceModel) => aiServiceModel.version_id === modelId)?.name;
            if (datasetName && modelName) {
                setName(`${modelName}: ${datasetName}`);
            }
        }
    }, [modelId, datasetId]);
    useEffect(() => {
        setModelId(defaultModelId);
    }, [defaultModelId]);
    useEffect(() => {
        setDatasetId(defaultDatasetId);
    }, [defaultDatasetId]);
    const isValidForm = Boolean(modelId) && (Boolean(datasetId) || evaluationTask !== "custom") && Boolean(name);
    const handleEvaluateModel = async () => {
        if (!modelId || !name) {
            return;
        }
        let placeholderDatasetId = -1;
        if (!datasetId) {
            if (evaluationTask == "custom") {
                return;
            }
            const exampleSetName = MODEL_EVALUATION_TASK_LABEL_MAP[evaluationTask];
            let placeholderDataset = Object.values(exampleSetSelector).find((exampleSet) => exampleSet.metadata.is_placeholder && exampleSet.name === exampleSetName);
            if (placeholderDataset === undefined) {
                placeholderDataset = await dispatch(contentGenerationReducer.createExampleSet({
                    folder_id: selectedFolderId,
                    name: MODEL_EVALUATION_TASK_LABEL_MAP[evaluationTask],
                    description: "",
                    metadata: {
                        is_placeholder: true,
                        is_hidden: true
                    }
                }));
            }
            placeholderDatasetId = placeholderDataset.id;
        }
        updateIsLoading(true);
        try {
            await dispatch(fineTuningReducer.trainFineTuningExecution({
                base_model_version_id: modelId,
                example_set_id: datasetId ?? placeholderDatasetId,
                name,
                description,
                type: FineTuningExecutionType.EVALUATION,
                parameters: {
                    type: evaluationTask
                }
            }));
            updateIsLoading(false);
        }
        finally {
            onOpenChange(false);
        }
    };
    return (React.createElement(Dialog, { open: open, onOpenChange: onOpenChange },
        React.createElement(DialogContent, { forceMount: true },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Evaluate a Post Trained Model")),
            React.createElement("div", null,
                React.createElement("div", { style: { marginBottom: "20px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Model to Evaluate"),
                    React.createElement(Select, { placeholder: "Choose Model", options: modelOptions, value: modelOptions.find((o) => o.value === modelId), onChange: (val) => {
                            if (!isOptionType(val)) {
                                return;
                            }
                            setModelId(val.value);
                        }, menuPlacement: "bottom" })),
                React.createElement("div", { style: { marginBottom: "20px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Evaluation Method"),
                    React.createElement(Select, { value: EVALUATION_TASK_MAPPER.map(evaluationTask), options: EVALUATION_TASK_MAPPER.mapAll(keysOf(MODEL_EVALUATION_TASK_LABEL_MAP)), onChange: selectOnChangeWrapperIfPresent(updateEvaluationTask) })),
                React.createElement("div", { style: { marginBottom: "20px", display: evaluationTask == "custom" ? undefined : "none" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Evaluation Dataset"),
                    React.createElement(Select, { placeholder: "Choose Dataset", options: exampleSetsOptions, value: exampleSetsOptions.find((o) => o.value === datasetId), isDisabled: evaluationTask !== "custom", onChange: (val) => {
                            if (!isOptionType(val)) {
                                return;
                            }
                            setDatasetId(val.value);
                        }, menuPlacement: "bottom" })),
                React.createElement("div", { style: { marginBottom: "20px" } },
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Evaluation Name"),
                    React.createElement(LineInput, { placeholder: "Evaluation Name", value: name, onChange: (value) => setName(value) })),
                React.createElement("div", null,
                    React.createElement("div", { style: { marginBottom: "5px" } }, "Evaluation Description"),
                    React.createElement("textarea", { className: styles.textArea, placeholder: "Evaluation Description", value: description, onChange: (e) => setDescription(e.target.value) }))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { disabled: isLoading, type: "secondary", onClick: () => onOpenChange(false) }, "Cancel"),
                React.createElement(Button, { isLoading: isLoading, disabled: !isValidForm || isLoading, type: "primary", onClick: () => handleEvaluateModel() }, "Evaluate Model")))));
};
export default ModelEvaluationModal;
